
import { AsYouType, getCountries, getCountryCallingCode, isPossiblePhoneNumber, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import { countryDisplayName } from '@/lib/geo';
import { isPlaceholderDigits } from './phone-placeholder';

export { isPlaceholderDigits };

/** Privacy-safe rendering for a verified login number. Keep this shared so settings, recovery
 * and future account surfaces never invent incompatible masking rules. */
export const obscurePhone = (phone: string) =>
  phone.length > 6 ? `${phone.slice(0, 3)}•••••${phone.slice(-4)}` : phone;

/**
 * Country data for the phone field's country picker.
 *
 * Everything here comes from libphonenumber-js (dial codes, per-country masks,
 * length rules) and `Intl.DisplayNames` (localized names), so no table of country
 * names or dial codes is maintained by hand — the failure this whole change exists
 * to prevent is a number being interpreted as a country nobody stated, and a
 * hand-kept table is exactly how that creeps back in.
 *
 * The stored value is always canonical E.164 (`+14389280106`): the API's
 * `normalizePhone` returns a `+`-prefixed value verbatim, so a canonical column
 * means the send path never has to guess a country again.
 */

/** Countries shown above the separator. The markets in `market_fee_settings`
 *  (BR, US, CA, MX, BS) plus the two the site's own languages point at most often
 *  (PT, ES, AR) — a Brazilian, who is the overwhelming majority, sees BR first. */
export const PINNED_COUNTRIES: readonly CountryCode[] = [
  'BR',
  'PT',
  'US',
  'CA',
  'MX',
  'ES',
  'AR',
] as const;

/** Fallback when IP geolocation says nothing — the home market, matching
 *  `marketForCountry`'s own "'BR' when undetectable" rule. */
export const DEFAULT_PHONE_COUNTRY: CountryCode = 'BR';

export interface PhoneCountryOption {
  /** ISO 3166-1 alpha-2, e.g. 'CA'. */
  code: CountryCode;
  /** Localized country name, e.g. 'Canadá'. */
  name: string;
  /** Calling code WITHOUT the plus, e.g. '1'. */
  dial: string;
  /** True for the pinned group, so the picker can draw its separator. */
  pinned: boolean;
}

/** Every selectable country, pinned group first (in PINNED order), then the rest
 *  alphabetically by localized name. Memoized per locale: 245 entries with an
 *  Intl.DisplayNames lookup each is not something to redo on every keystroke. */
const optionsCache = new Map<string, PhoneCountryOption[]>();

export function phoneCountryOptions(locale: string): PhoneCountryOption[] {
  const cached = optionsCache.get(locale);
  if (cached) return cached;

  const build = (code: CountryCode): PhoneCountryOption => ({
    code,
    name: countryDisplayName(code, locale),
    dial: getCountryCallingCode(code),
    pinned: PINNED_COUNTRIES.includes(code),
  });

  const pinned = PINNED_COUNTRIES.filter((c) => getCountries().includes(c)).map(build);
  const rest = getCountries()
    .filter((c) => !PINNED_COUNTRIES.includes(c))
    .map(build)
    .sort((a, b) => a.name.localeCompare(b.name, locale));

  const all = [...pinned, ...rest];
  optionsCache.set(locale, all);
  return all;
}

/**
 * Split a stored value into (country, national digits) for editing.
 *
 * A canonical E.164 value resolves exactly — including which +1 country it is, since
 * libphonenumber reads the area code (438 is Montreal, so Canada, not the United
 * States). A legacy value with no country (`85996993146`, `(85) 99699-3146`) is read
 * with `fallback` as the assumed country, which is the same interpretation the API's
 * `normalizePhone` already applies to it: the migration backfills these, so this path
 * only covers rows written by something other than the API.
 */
export function splitPhone(
  stored: string | null | undefined,
  fallback: CountryCode = DEFAULT_PHONE_COUNTRY,
): { country: CountryCode; national: string } {
  const raw = (stored ?? '').trim();
  if (!raw) return { country: fallback, national: '' };
  const parsed = parsePhoneNumberFromString(raw, raw.startsWith('+') ? undefined : fallback);
  if (!parsed?.country) {
    // Unparseable (a partial number mid-typing, junk): keep the digits so the user
    // sees what is stored rather than a silently emptied field.
    return { country: fallback, national: raw.replace(/[^\d]/g, '') };
  }
  return { country: parsed.country, national: parsed.nationalNumber };
}

/** Group the national digits the way the selected country writes them, as you type. */
export function formatNational(national: string, country: CountryCode): string {
  const digits = national.replace(/[^\d]/g, '');
  if (!digits) return '';
  return new AsYouType(country).input(digits);
}

/**
 * The value that gets stored: canonical E.164, or null when the digits cannot be one.
 *
 * Deliberately `isPossible` (right length for the country) rather than `isValid`
 * (matches an assigned range): libphonenumber's range metadata lags real carrier
 * allocations, and refusing a legitimately new number on the signup form is a worse
 * failure than accepting an unassigned one. Placeholder digits are the one exception
 * (see `isPlaceholderDigits`): the right length, but not a phone.
 */
export function toE164(national: string, country: CountryCode): string | null {
  const digits = national.replace(/[^\d]/g, '');
  if (!digits || isPlaceholderDigits(digits)) return null;
  const candidate = `+${getCountryCallingCode(country)}${digits}`;
  return isPossiblePhoneNumber(candidate) ? candidate : null;
}

export type { CountryCode };
